<template>
    <div class="view-report-static-header">
        <div class="view-report-static-header__info">
            <div class="view-report-static-header__info__title">
                <ChevronLeft
                    class="view-report-static-header__info__title-icon"
                    @click="onBackClick"
                />
                <span>{{ pageTitle }}</span>
            </div>
            <span
                v-if="pageSubtitle"
                class="view-report-static-header__info__subtitle"
            >
                {{ pageSubtitle }}
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ChevronLeft } from 'lucide-vue-next'
import { computed, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useReportGetQuery } from '@/api/queries/reports/useReportGetQuery'
import { ReportTypes } from '@/configuration/report/report-types'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { useGeneratorDetailsQuery } from '@/api/queries/generators/useGeneratorDetailsQuery'
import { RouteName } from '@/configuration/route/route-names'

const props = defineProps<{
    componentSid: string
    reportNumber: string
    reportType: number
}>()

const { componentSid, reportNumber, reportType } = toRefs(props)

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const { data } = useReportGetQuery(reportNumber, reportType, componentSid)

const generatorId = computed(() => {
    if (!data.value) {
        return undefined
    }

    const firstAnalysisWithComponentSid = data.value?.data?.find((e) => !!e.componentSid)
    return firstAnalysisWithComponentSid?.componentSid ?? undefined
})

const { data: generatorDetailsData } = useGeneratorDetailsQuery(generatorId)

const pageTitle = computed(() => {
    if (route.name === RouteName.GeneratorsReportsViewCondition) {
        return t('reports.detailsPage.pageTitle.reportTitle.conditionStatus', {
            componentId: generatorId.value ?? '-',
            reportNumber: reportNumber.value,
        })
    }

    return t('reports.imagesPage.pageTitle.reportTitle.default', {
        reportNumber: reportNumber.value,
    })
})

const reportDate = computed(() => data.value?.data?.[0]?.samplingDate)

const pageSubtitle = computed(() => {
    if (!generatorDetailsData.value) {
        return ''
    }

    const plantGroupName = generatorDetailsData.value.riverGroupName
    const plantName = generatorDetailsData.value.powerPlantName
    const generatorName = generatorDetailsData.value.name
    const generatorSid = generatorDetailsData.value.generatorID
    const reportTypeString = t(ReportTypes.find((e) => e.value === reportType.value)?.labelKey ?? '-')

    const reportDateFormatted = format(new Date(reportDate.value ?? new Date()), DATE_FORMAT)

    return `${plantGroupName} / ${plantName} / ${generatorName} / ${generatorSid} / ${reportTypeString} / ${reportDateFormatted}`
})

const onBackClick = () => {
    router.back()
}
</script>

<style scoped lang="scss">
.view-report-static-header {
    @include spacing-page;

    display: flex;
    justify-content: space-between;
    align-items: center;

    background-color: $pbl-foreground;
    border-bottom: 1px solid $pbl-border-muted;

    &__info {
        display: flex;
        flex-direction: column;
        gap: $spacing-s;

        &__title {
            display: flex;
            align-items: center;

            gap: $spacing-2xs;

            &-icon {
                margin-bottom: -2px;
                height: 20px;
                width: 20px;

                cursor: pointer;
            }

            span {
                font-size: 1.25rem;
                font-weight: 500;
            }
        }
    }
}
</style>
